import React, { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { METRICS } from "./usePerformance";
import { translateFr as tfr } from "@/i18n";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."].map((s) => tfr(s));
const fmtDay = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

/* Stable tooltip: single component identity, pointer-events none, no remount per move. */
function PerfTooltip({ active, payload, label, metricLabel }) {
  if (!active || !payload?.length) return null;
  const cur = payload.find((p) => p.dataKey === "value");
  const prev = payload.find((p) => p.dataKey === "prev");
  const delta = cur && prev && prev.value != null ? cur.value - prev.value : null;
  const pct = delta != null && prev.value ? ((100 * delta) / Math.abs(prev.value)).toFixed(1) : null;
  return (
    <div className="chart-tooltip-surface pointer-events-none rounded-[10px] px-3 py-2.5 min-w-[170px]" role="status" data-testid="perf-tooltip">
      <p className="text-[12px] font-semibold">{fmtDay(label)}</p>
      {cur && (
        <p className="text-xs text-[color:var(--chart-tooltip-muted)] mt-1 flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-[#EF4444]" aria-hidden /> {tfr("Période actuelle :")} <span className="font-medium tabular-nums text-[color:var(--chart-tooltip-text)]">{cur.value}</span>
        </p>
      )}
      {prev && prev.value != null && (
        <p className="text-xs text-[color:var(--chart-tooltip-muted)] mt-0.5 flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-[#6B7280]" aria-hidden /> {tfr("Période précédente :")} <span className="font-medium tabular-nums text-[color:var(--chart-tooltip-text)] opacity-80">{prev.value}</span>
        </p>
      )}
      {delta != null && (
        <p className={`text-[11px] mt-1 font-medium tabular-nums ${delta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
          {delta >= 0 ? "+" : ""}{Math.round(delta * 10) / 10}{pct != null ? ` (${delta >= 0 ? "+" : ""}${pct} %)` : ""}
        </p>
      )}
    </div>
  );
}

export default function TimelineCard({ metric, onMetricChange, timeline, comparisonLabel, loading }) {
  const metricDef = METRICS.find((m) => m.id === metric) || METRICS[0];
  const rows = useMemo(() => {
    const prevByIdx = timeline?.previous || [];
    return (timeline?.current || []).map((p, i) => ({
      date: p.date, value: p.value, prev: prevByIdx[i]?.value ?? null,
    }));
  }, [timeline]);
  const hasPrev = rows.some((r) => r.prev != null);

  return (
    <section className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-4 sm:p-6" data-testid="perf-timeline-card">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">{tfr("Évolution de la performance")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{tfr("Suivez la progression de vos indicateurs clés dans le temps.")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
          <span className="text-xs text-muted-foreground shrink-0">{tfr("Afficher :")}</span>
          <Select value={metric} onValueChange={onMetricChange}>
            <SelectTrigger className="h-9 flex-1 sm:flex-none sm:w-[220px] text-xs" data-testid="perf-metric-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METRICS.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`perf-metric-${m.id}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-2" aria-hidden>
        <span className="flex items-center gap-1.5"><span className="h-[3px] w-5 rounded-full bg-[#EF4444]" /> {tfr("Période actuelle")}</span>
        {hasPrev && <span className="flex items-center gap-1.5"><span className="h-0 w-5 border-t-2 border-dashed border-[#6B7280]" /> {comparisonLabel || tfr("Période précédente")}</span>}
      </div>

      <div className="h-[230px] sm:h-[280px]" role="img" aria-label={`${tfr("Graphique :")} ${metricDef.label}, ${rows.length} ${tfr("points sur la période sélectionnée")}`}>
        {loading ? (
          <div className="h-full rounded-xl bg-muted/60 animate-pulse" data-testid="perf-timeline-skeleton" />
        ) : rows.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">{tfr("Aucune donnée pour la période sélectionnée.")}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={40} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} width={46} allowDecimals={false} />
              <Tooltip content={<PerfTooltip metricLabel={metricDef.label} />} isAnimationActive={false} cursor={{ stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35, strokeDasharray: "3 3" }} />
              {hasPrev && (
                <Line type="monotone" dataKey="prev" stroke="#6B7280" strokeDasharray="5 4" strokeWidth={1.5}
                  dot={{ r: 2, fill: "#6B7280", strokeWidth: 0 }} activeDot={{ r: 3 }} isAnimationActive={false} />
              )}
              <Line type="monotone" dataKey="value" stroke="#EF4444" strokeWidth={2}
                dot={{ r: 2.5, fill: "#EF4444", strokeWidth: 0 }} activeDot={{ r: 4, fill: "#EF4444" }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
